import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface ScenarioData {
  id: string;
  typeId: string;
  title: string;
  description: string | null;
  iconName: string | null;
  colorTheme: string | null;
  sortOrder: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateScenarioData {
  typeId: string;
  title: string;
  description?: string;
  iconName?: string;
  colorTheme?: string;
  sortOrder?: number;
}

export function useScenarios() {
  const queryClient = useQueryClient();

  const { data: scenarios = [], isLoading, isError } = useQuery({
    queryKey: ["scenarios"],
    queryFn: async () => {
      const response = await fetch("/api/scenarios");
      if (!response.ok) {
        throw new Error("Failed to fetch scenarios");
      }
      return response.json() as Promise<ScenarioData[]>;
    },
    staleTime: 1000 * 60 * 5,
  });

  const createScenario = useMutation({
    mutationFn: async (data: CreateScenarioData) => {
      const response = await fetch("/api/scenarios", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to create scenario");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scenarios"] });
    },
  });

  const updateScenario = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CreateScenarioData> & { id: string }) => {
      const response = await fetch(`/api/scenarios/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        throw new Error("Failed to update scenario");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scenarios"] });
    },
  });

  const deleteScenario = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/scenarios/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete scenario");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scenarios"] });
    },
  });

  return {
    scenarios,
    isLoading,
    isError,
    createScenario: createScenario.mutate,
    updateScenario: updateScenario.mutate,
    deleteScenario: deleteScenario.mutate,
    createScenarioAsync: createScenario.mutateAsync,
    updateScenarioAsync: updateScenario.mutateAsync,
    deleteScenarioAsync: deleteScenario.mutateAsync,
    isCreating: createScenario.isPending,
    isUpdating: updateScenario.isPending,
    isDeleting: deleteScenario.isPending,
  };
}
